const alignItems = {
    name: "align-items",
    path: "/align-items",
    title: "align-items",
    description: "Utilities for controlling how grid items are aligned along their block axis.",
    value: {
        "align-items": {
            "items-start": "flex-start",
            "items-end": "flex-end",
            "items-center": "center",
            "items-baseline": "baseline",
            "items-stretch": "stretch",
            "items-normal": "normal",
            "items-end-safe": "safe flex-end",
            "items-center-safe": "safe center",
            "items-baseline-last": "last baseline",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="grid grid-columns-3 h-32 items-start gap-4">
  <div class="bg-color-pink-500 text-white p-2 h-8">01</div>
  <div class="bg-color-pink-500 text-white p-2 h-16">02</div>
  <div class="bg-color-pink-500 text-white p-2 h-12">03</div>
</div>

<div class="grid grid-columns-3 h-32 items-end gap-4 mt-6">
  <div class="bg-color-pink-500 text-white p-2 h-8">01</div>
  <div class="bg-color-pink-500 text-white p-2 h-16">02</div>
  <div class="bg-color-pink-500 text-white p-2 h-12">03</div>
</div>

<div class="grid grid-columns-3 h-32 items-center gap-4 mt-6">
  <div class="bg-color-pink-500 text-white p-2 h-8">01</div>
  <div class="bg-color-pink-500 text-white p-2 h-16">02</div>
  <div class="bg-color-pink-500 text-white p-2 h-12">03</div>
</div>

<div class="grid grid-columns-3 h-32 items-baseline gap-4 mt-6">
  <div class="bg-color-pink-500 text-white p-2 h-8 flex items-end">01</div>
  <div class="bg-color-pink-500 text-white p-2 h-16 flex items-end">02</div>
  <div class="bg-color-pink-500 text-white p-2 h-12 flex items-end">03</div>
</div>

<div class="grid grid-columns-3 h-32 items-stretch gap-4 mt-6">
  <div class="bg-color-pink-500 text-white p-2">01</div>
  <div class="bg-color-pink-500 text-white p-2">02</div>
  <div class="bg-color-pink-500 text-white p-2">03</div>
</div>
                `
            },
        },
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">items-*</code> utilities to control how grid items are aligned along their block axis.
            </p>
            <div class="space-y-10">
                <div>
                    <small class="text-center block mb-4">items-start</small>
                    <div class="relative h-32">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-4 items-start font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-8">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-16">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-12">03</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">items-end</small>
                    <div class="relative h-32">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-4 items-end font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-8">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-16">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-12">03</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">items-center</small>
                    <div class="relative h-32">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-4 items-center font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-8">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-16">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-12">03</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">items-baseline</small>
                    <div class="relative h-32">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-4 items-baseline font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-8 flex items-end">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-16 flex items-end">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg h-12 flex items-end">03</div>
                        </div>
                    </div>
                </div>
                <div>
                    <small class="text-center block mb-4">items-stretch</small>
                    <div class="relative h-32">
                        <div class="absolute inset-0 z-0">
                            <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                        </div>
                        <div class="grid grid-columns-3 h-full gap-4 items-stretch font-mono text-sm leading-6 font-bold text-color-white relative">
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg">01</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg">02</div>
                            <div class="bg-color-pink-500 p-2 flex justify-center items-center  rounded-lg">03</div>
                        </div>
                    </div>
                </div>
            </div>
            `
        },
    ]
};

export default alignItems;


